import { useRef } from 'react';
import * as THREE from 'three';
import { useFrame, ThreeEvent } from '@react-three/fiber';

interface DeviceModelProps {
  color: THREE.Color;
  isSelected: boolean;
  scale?: number;
  onClick?: (e: ThreeEvent<MouseEvent>) => void;
  onPointerDown?: (e: ThreeEvent<PointerEvent>) => void;
  onPointerUp?: (e: ThreeEvent<PointerEvent>) => void;
  onPointerOver?: (e: ThreeEvent<PointerEvent>) => void;
  onPointerOut?: (e: ThreeEvent<PointerEvent>) => void;
}

export function DeviceModel({
  color,
  isSelected,
  scale = 1,
  onClick,
  onPointerDown,
  onPointerUp,
  onPointerOver,
  onPointerOut,
}: DeviceModelProps) {
  const groupRef = useRef<THREE.Group>(null);
  const mainBodyRef = useRef<THREE.Mesh>(null);

  // Subtle floating animation
  useFrame((state) => {
    if (groupRef.current && !isSelected) {
      groupRef.current.position.y = Math.sin(state.clock.elapsedTime * 2) * 0.1;
    }
  });

  return (
    <group
      ref={groupRef}
      scale={scale}
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerOver={onPointerOver}
      onPointerOut={onPointerOut}
    >
      {/* Main body */}
      <mesh ref={mainBodyRef} castShadow receiveShadow>
        <boxGeometry args={[1, 0.8, 0.6]} />
        <meshStandardMaterial
          color={color}
          metalness={0.5}
          roughness={0.2}
          emissive={isSelected ? color : 0x000000}
          emissiveIntensity={isSelected ? 0.2 : 0}
        />
      </mesh>

      {/* Top panel */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <boxGeometry args={[0.8, 0.1, 0.4]} />
        <meshStandardMaterial
          color={0x333333}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>

      {/* Status LED */}
      <mesh position={[0.3, 0.45, 0.2]} castShadow>
        <sphereGeometry args={[0.05, 16, 16]} />
        <meshStandardMaterial
          color={0x00ff00}
          emissive={0x00ff00}
          emissiveIntensity={0.5}
        />
      </mesh>

      {/* Antenna */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.02, 0.3, 8]} />
        <meshStandardMaterial
          color={0x666666}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>

      {/* Base/Stand */}
      <mesh position={[0, -0.4, 0]} castShadow>
        <boxGeometry args={[0.6, 0.1, 0.4]} />
        <meshStandardMaterial
          color={0x222222}
          metalness={0.5}
          roughness={0.5}
        />
      </mesh>
    </group>
  );
} 